import { useEffect, useState } from "react"
import { useStore } from "../../context/auth/auth"
import LogoReversed from "../../images/trimble-logo-reversed.png"
import { formatLink } from "../functions"
import { formatPageLinks } from "../organism-formatting/formatPageLinks"
import { productsEndpoint, accountsEndpoint } from "../config/ecomConfig"
import { useQueryParams, StringParam } from "use-query-params"
import { getTrialFormId } from "../functions/getTrialForm"
import { refreshAuthToken } from "../functions/refreshAuthToken"
import { Link } from "gatsby";
import { createProductDetailPageLink, createProductTrialPageLink } from "../../components/CTAButtons/CTAButtonsConnector";

const config =
  process.env.GATSBY_CONFIG_OVERRIDE &&
  JSON.parse(process.env.GATSBY_CONFIG_OVERRIDE)

export const useFlyout = (
  flyoutData,
  siteNavigationData,
  currentLocale,
  isAuthenticated,
  auth,
  userData,
  logoutUser,
  path,
  query,
  homepageSlug,
  currentSpaceId,
  products,
  siteIdentifier,
  isDx,
  isOwner,
  signUpQuery,
  isAdmin,
  isEcom,
  ecommerceSettings
) => {
  const [queryUrl, _setQuery] = useQueryParams({
    search: StringParam,
  })
  const [state, dispatch] = useStore()
  const [isAccountSwitcherOpen, setAccountSwitcherOpen] = useState(false)
  const [isOwnerAccount, setOwnerAccount] = useState(false)
  const [isAdminAccount, setAdminAccount] = useState(false)

  useEffect(() => {
    if (state?.activeAccount?.role?.includes("OWNER")) {
      setOwnerAccount(true)
      setAdminAccount(false)
    }
    if (state?.activeAccount?.role?.includes("ADMIN")) {
      setOwnerAccount(false)
      setAdminAccount(true)
    }
    if (state?.activeAccount?.role?.includes("USER")) {
      setOwnerAccount(false)
      setAdminAccount(false)
    }
  }, [state?.activeAccount])

  const handleAccountChange = accountId => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    })
    if (params?.account) {
      _setQuery({ account: undefined }, "replaceIn")
    }

    sessionStorage.removeItem("alertDismissed")
    const account = state?.accounts?.find(d => accountId === d?.id)
    dispatch({ type: "SET_ACCOUNT_ID", payload: accountId })
    dispatch({
      type: "SET_ACTIVE_ACCOUNT",
      payload: account,
    })
    if (account?.address?.country) {
      localStorage.setItem("country", account?.address?.country)
    }
    sessionStorage.setItem("accountSelected", accountId)
  }

  const setAccountAsDefault = account => {
    fetch(`${accountsEndpoint}/accounts/set-default-account`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-account-id": account,
        Authorization: `Bearer ${
          JSON.parse(localStorage?.getItem("user-trial-data"))?.id
        }`,
      },
    })
      .then(response => response.json())
      .then(result => {
        if (result?.status === "success") {
          // We need to update "accounts" state without re-fetching all the accounts so the UI will update as expected (with new default account rendering and outdated default account no longer showing "default")
          const cloneAccounts = JSON.parse(JSON.stringify(state.accounts))

          const removeOldDefaultFromUI = cloneAccounts.map(act => {
            if (act.default === true && act.id !== account) {
              act.default = false
            }
            if (account === act?.id) {
              act.default = true
            }
            return act
          })

          // Set updated accounts as state with newly default account selected and old default removed
          dispatch({ type: "SET_ACCOUNTS", payload: removeOldDefaultFromUI })

          const accountData = cloneAccounts?.find(d => account === d?.id)

          // update current account
          dispatch({ type: "SET_ACCOUNT_ID", payload: accountData?.id })

          // update current account
          dispatch({
            type: "SET_ACTIVE_ACCOUNT",
            payload: accountData,
          })
        }

        // getAccounts(JSON.parse(localStorage?.getItem("user-trial-data"))?.id)
        return result
      })
      .catch(error => {
        // this will error if the user has no cart and is logged in with valid tokens. Instead of displaying an error message, we insure the cart is null.
      })
  }
  const isPublicPage = config?.makeAllPagesPublic
    ? true
    : siteNavigationData?.isPublicPage

  const topNavColor =
    config?.enableLogin &&
    isPublicPage &&
    !isAuthenticated &&
    config?.topNavThemeLoggedOut
      ? config?.topNavThemeLoggedOut
      : siteNavigationData?.topNavTheme
  let links

  if (process.env.GATSBY_AXP_ENABLED) {
    if (isDx && isOwnerAccount && localStorage?.getItem("id_link")) {
      links = [
        {
          url:
            process.env.GATSBY_ENV === "staging"
              ? "https://stage.myprofile.trimblecloud.com/"
              : "https://myprofile.trimble.com/",
          text: flyoutData?.profileLabel || "My Profile",
        },
        {
          url: process.env.GATSBY_PRODUCTS_LINK
            ? process.env.GATSBY_PRODUCTS_LINK
            : "https://accounts.trimble.com/products",
          text: flyoutData?.myProductsLabel || "My Products",
        },
        {
          url: process.env.GATSBY_SUPPORT_LINK,
          text: flyoutData?.supportLabel || "Support",
        },
        {
          url:
            process.env.GATSBY_ENV === "staging"
              ? `https://sit.accounts.trimble.com/${
                  localStorage.getItem("id_link") !== state?.acccountId
                    ? state.accountId
                    : localStorage.getItem("id_link")
                }/subscriptions`
              : `https://accounts.trimble.com/${localStorage.getItem(
                  "id_link"
                )}/subscriptions`,
          text: flyoutData?.manageAccountsLabel || "Manage Accounts",
        },
      ]
    } else if (isDx && !isOwnerAccount) {
      links = [
        {
          url:
            process.env.GATSBY_ENV === "staging"
              ? "https://stage.myprofile.trimblecloud.com/"
              : "https://myprofile.trimble.com/",
          text: flyoutData?.profileLabel || "My Profile",
        },
        {
          url: process.env.GATSBY_PRODUCTS_LINK
            ? process.env.GATSBY_PRODUCTS_LINK
            : "https://accounts.trimble.com/products",
          text: flyoutData?.myProductsLabel || "My Products",
        },
        {
          url: process.env.GATSBY_SUPPORT_LINK,
          text: flyoutData?.supportLabel || "Support",
        },
      ]
    } else {
      links = [
        {
          url:
            process.env.GATSBY_ENV === "staging"
              ? "https://stage.myprofile.trimblecloud.com/"
              : "https://myprofile.trimble.com/",
          text: flyoutData?.profileLabel || "My Profile",
        },
        {
          url: process.env.GATSBY_PRODUCTS_LINK
            ? process.env.GATSBY_PRODUCTS_LINK
            : "https://accounts.trimble.com/products",
          text: flyoutData?.myProductsLabel || "My Products",
        },
      ]
    }
  }
  if (!process.env.GATSBY_AXP_ENABLED) {
    links = [
      {
        url: process.env.GATSBY_ENV === "staging"
          ? "https://stage.myprofile.trimblecloud.com/"
          : "https://myprofile.trimble.com/",
        text: flyoutData?.profileLabel || "My Profile",
      },
      {
        url: process.env.GATSBY_PRODUCTS_LINK
          ? process.env.GATSBY_PRODUCTS_LINK
          : "https://accounts.trimble.com/products",
        text: flyoutData?.myProductsLabel || "My Products",
      },
    ]
  }
  const roleMapper = role => {
    if (!role) {
      return ""
    }

    if (role === "ACCOUNT_OWNER") {
      return ecommerceSettings?.accountOwner || "Account Owner"
    }
    if (role === "SECONDARY_ACCOUNT_OWNER") {
      return (
        ecommerceSettings?.secondaryAccountOwner || "Secondary Account Owner"
      )
    }

    if (role === "COMPANY_ADMIN") {
      return ecommerceSettings?.licenseAdmin || "License Admin"
    }

    if (role === "PRODUCT_USER") {
      return ecommerceSettings?.productUser || "Product User"
    }
    return role
  }

  const formatAccountsForAccountSwitcher = accounts => {
    return accounts?.map(account => {
      return { ...account, formattedRole: roleMapper(account?.role) }
    })
  }

  return {
    login: !siteNavigationData?.disableLogin ? true : false,
    authenticated: !siteNavigationData?.disableLogin && auth ? auth : false,
    profile: {
      image: null,
      name: state.activeAccount
        ? state.activeAccount?.name
        : userData
        ? `${userData?.fName} ${userData?.lName}`
        : null,
      role: state.activeAccount?.role,
      organization: state.activeAccount?.organization,
    },
    searchPlaceholder: flyoutData?.searchPlaceholder,
    disableSearch: flyoutData?.disableSearch,
    logo: {
      url: flyoutData?.trimbleLogo?.image?.file?.url || LogoReversed,
      altText: flyoutData?.trimbleLogo?.altText || "Trimble Logo",
      link: {
        url: `/${currentLocale}/`,
      },
    },
    mobileLabel: flyoutData?.mobileLabel,
    createAccount: {
      text: flyoutData?.createAccountLabel,
      signInBtnMethod:
        !siteNavigationData?.disableLogin && typeof window !== "undefined"
          ? () => window.open(`${signUpQuery}${path}`, "_self")
          : () => {},
    },
    signInLabel: flyoutData?.signInLabel,
    signOutLabel: flyoutData?.signOutLabel,
    signInBtnMethod:
      !siteNavigationData?.disableLogin && typeof window !== "undefined"
        ? () => window.open(`${query}${path}`, "_self")
        : () => {},

    signOutBtnMethod: !siteNavigationData?.disableLogin ? logoutUser : () => {},

    myAccountLinks: {
      title: flyoutData?.accountsHeading,
      links: !siteNavigationData?.disableLogin ? links : [],
    },
    marketingLinks: {
      title: flyoutData?.marketingHeading,
      links:
        flyoutData?.links
          ?.filter(flyoutLink => {
            return (
              flyoutLink?.__typename === "ContentfulExternalLink" ||
              flyoutLink?.__typename === "ContentfulPageBase" || 
              flyoutLink?.__typename === "ContentfulSimpleLink" ||
              flyoutLink?.__typename === "ContentfulPageGeneral" ||
              flyoutLink?.__typename === "ContentfulProductDetailPage" ||
              flyoutLink?.__typename === "ContentfulProductTrialPage"
            )
          })
          ?.map(flyoutLink =>
            flyoutLink?.__typename === "ContentfulPageBase" ||
            flyoutLink?.__typename === "ContentfulPageGeneral"
              ? {
                url: formatLink(
                  flyoutLink?.slug || flyoutLink?.url,
                  currentLocale,
                  null,
                  homepageSlug
                ),
                text: flyoutLink?.text || flyoutLink?.title || flyoutLink?.name || flyoutLink?.pageName || flyoutLink?.productName || flyoutLink?.publicTitle,
                type: "link",
                Link: Link
              }
              : flyoutLink?.__typename === "ContentfulExternalLink" ||
                flyoutLink?.__typename === "ContentfulSimpleLink"
              ? {
                  url: formatLink(
                    flyoutLink?.url || flyoutLink?.dynamicPageReference?.url,
                    currentLocale,
                    flyoutLink?.__typename,
                    homepageSlug
                  ),
                  text: flyoutLink?.text || flyoutLink?.title || flyoutLink?.linkText,
                  type: "link",
                  Link: flyoutLink?.__typename === "ContentfulExternalLink" ? null : Link
                }
              : flyoutLink?.__typename === "ContentfulProductTrialPage"
              ? createProductTrialPageLink(currentLocale, flyoutLink)
              : flyoutLink?.__typename === "ContentfulProductDetailPage"
              ? createProductDetailPageLink(currentLocale, flyoutLink)
              : null
          ) || [],
    },
    customLinks: {
      enabled: flyoutData?.showCustomLinks,
      title: flyoutData?.customLinksHeading,
      links: flyoutData?.customLinks?.map(flyoutLink => {
        return flyoutLink?.__typename === "ContentfulPageBase" ||
        flyoutLink?.__typename === "ContentfulPageGeneral"
          ? {
            url: formatLink(
              flyoutLink?.slug || flyoutLink?.url,
              currentLocale,
              null,
              homepageSlug
            ),
            text: flyoutLink?.text || flyoutLink?.title || flyoutLink?.name || flyoutLink?.pageName || flyoutLink?.publicTitle ,
            type: "link",
            Link: Link
          }
          : flyoutLink?.__typename === "ContentfulExternalLink" ||
            flyoutLink?.__typename === "ContentfulSimpleLink"
          ? {
              url: formatLink(
                flyoutLink?.linkUrl || flyoutLink?.url || flyoutLink?.dynamicPageReference?.url,
                currentLocale,
                flyoutLink?.__typename,
                homepageSlug
              ),
              text: flyoutLink?.text || flyoutLink?.title || flyoutLink?.linkText,
              type: "link",
              Link: flyoutLink?.__typename === "ContentfulExternalLink" ? null : Link
            }
          : flyoutLink?.__typename === "ContentfulProductTrialPage"
          ? createProductTrialPageLink(currentLocale, flyoutLink)
          : flyoutLink?.__typename === "ContentfulProductDetailPage"
          ? createProductDetailPageLink(currentLocale, flyoutLink)
          : null
      }),
    },
    ecommerceLinks: {
      enabled: flyoutData?.showEcommerceLinks,
      title: flyoutData?.ecommerceHeading,
      links: [
        { url: "#", text: flyoutData?.billingInfoLabel },
        { url: "#", text: flyoutData?.orderHistoryLabel },
        { url: "#", text: flyoutData?.myCartLabel },
      ],
    },
    legalLinks: {
      links: flyoutData?.legalLinks?.map(link =>
        link?.__typename === "ContentfulPageBase" ||
        link?.__typename === "ContentfulPageGeneral"
          ? {
            url: formatLink(
              link?.slug || link?.url,
              currentLocale,
              null,
              homepageSlug
            ),
            text: link?.text || link?.title || link?.name || link?.pageName || link?.publicTitle ,
            type: "link",
            Link: Link
          }
          : link?.__typename === "ContentfulExternalLink" ||
            link?.__typename === "ContentfulSimpleLink"
          ? {
              url: formatLink(
                link?.linkUrl || link?.url || link?.dynamicPageReference?.url,
                currentLocale,
                link?.__typename,
                homepageSlug
              ),
              text: link?.text || link?.title || link?.linkText,
              type: "link",
              Link: link?.__typename === "ContentfulExternalLink" ? null : Link
            }
          : link?.__typename === "ContentfulProductTrialPage"
          ? createProductTrialPageLink(currentLocale, link)
          : link?.__typename === "ContentfulProductDetailPage"
          ? createProductDetailPageLink(currentLocale, link)
          : null
      ),
    },
    accountSwitcher: {
      isEnabled: true,
      title:
        !isOwnerAccount && !isAdminAccount
          ? "Account Switcher"
          : flyoutData?.accountSwitcherHeading || "Admin Console",
      accountId: state?.accountId,
      accounts: formatAccountsForAccountSwitcher(state?.accounts),
      isOpen: isAccountSwitcherOpen,
      setOpen: setAccountSwitcherOpen,
      onChange: handleAccountChange,
      selectLabel: state?.accounts?.length ? "Select" : "",
      defaultLabel: state?.activeAccount?.default ? "Default" : "",
      setAsDefaultLabel: state?.accounts?.length ? "Set as default" : "",
      updateDefaultAccount: setAccountAsDefault,
      links:
        process.env.GATSBY_AXP_ADMIN_CONSOLE === "true"
          ? isDx && isOwnerAccount
            ? [
                {
                  url:
                    process.env.GATSBY_ENV === "staging"
                      ? `https://uat.accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/settings`
                      : `https://accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/settings`,
                  text: flyoutData?.accountSettings || "Account Settings",
                },
                {
                  url:
                    process.env.GATSBY_ENV === "staging"
                      ? `https://uat.accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/subscriptions`
                      : `https://accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/subscriptions`,
                  text:
                    flyoutData?.manageSubscriptions || "Manage Subscriptions",
                },
                {
                  url:
                    process.env.GATSBY_ENV === "staging"
                      ? `https://uat.accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/users`
                      : `https://accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/users`,
                  text: flyoutData?.manageUsers || "Manage Users",
                },
              ]
            : isDx && isAdminAccount
            ? [
                {
                  url:
                    process.env.GATSBY_ENV === "staging"
                      ? `https://uat.accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/subscriptions`
                      : `https://accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/subscriptions`,
                  text:
                    flyoutData?.manageSubscriptions || "Manage Subscriptions",
                },
                {
                  url:
                    process.env.GATSBY_ENV === "staging"
                      ? `https://uat.accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/users`
                      : `https://accounts.trimble.com/${
                          localStorage.getItem("id_link") !== state?.acccountId
                            ? state.accountId
                            : localStorage.getItem("id_link")
                        }/users`,
                  text: flyoutData?.manageUsers || "Manage Users",
                },
              ]
            : []
          : [],
      hasAdminPrivileges: isOwner || isAdmin || isEcom,
    },
    oldVersion: false,
  }
}
